// src/context/ThemeContext.js
import React, { createContext, useState, useMemo, useEffect } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import MARCA from "../config/marca";

/* ===================== Defaults ===================== */
const defaultBrand = { primary: MARCA.colorPrimario, secondary: MARCA.colorSecundario };
// Con marca (ej.: telusgy) los colores elegidos se guardan aparte, así no se mezclan con los de otra empresa.
const BRAND_KEY = MARCA.id ? `brand_${MARCA.id}` : "brand";
const defaultFonts = {
  body:
    "Inter Variable, Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif",
  heading: "Inter Variable, Inter, sans-serif",
  mono: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
};

/* ===================== Storage helpers ===================== */
const safeGet = (k, fallback) => {
  try {
    const v = localStorage.getItem(k);
    return v ?? fallback;
  } catch {
    return fallback;
  }
};
const safeGetJSON = (k, fallback) => {
  try {
    const v = localStorage.getItem(k);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

const getStoredMode = () => safeGet("mode", "light");
const getStoredBrand = () => {
  const b = safeGetJSON(BRAND_KEY, defaultBrand);
  return b?.primary && b?.secondary ? b : defaultBrand;
};
const getStoredFonts = () => {
  const f = safeGetJSON("fonts", defaultFonts);
  return f?.body && f?.heading && f?.mono ? f : defaultFonts;
};
const getStoredCompact = () => safeGet("compact", "false") === "true";
const getStoredNavStyle = () => safeGet("navStyle", "sidebar"); // sidebar | rail | top

// Escala tipográfica accesible. 1 = tamaño de referencia del diseño.
export const TEXT_SCALES = [0.9, 1, 1.1, 1.2];
const getStoredTextScale = () => {
  const n = Number(safeGet("textScale", "1"));
  return TEXT_SCALES.includes(n) ? n : 1;
};
// "system" respeta prefers-reduced-motion del sistema operativo.
const getStoredMotion = () => {
  const v = safeGet("motion", "system");
  return ["system", "reduce", "full"].includes(v) ? v : "system";
};

/* ===================== Context ===================== */
export const ThemeContext = createContext({
  // color
  mode: "light",
  setMode: () => {},
  darkMode: false,
  toggleDarkMode: () => {},
  // brand & fonts
  brand: defaultBrand,
  setBrand: () => {},
  fonts: defaultFonts,
  setFonts: () => {},
  // density
  compact: false,
  setCompact: () => {},
  // navigation style
  navStyle: "sidebar",
  setNavStyle: () => {},
  // accesibilidad
  textScale: 1,
  setTextScale: () => {},
  motion: "system",
  setMotion: () => {},
  reducedMotion: false,
});

const ThemeContextProvider = ({ children }) => {
  /* ---------- State ---------- */
  const [mode, setMode] = useState(getStoredMode()); // "light" | "dark" | "system"
  const [brand, setBrand] = useState(getStoredBrand());
  const [fonts, setFonts] = useState(getStoredFonts());
  const [compact, setCompact] = useState(getStoredCompact());
  const [navStyle, setNavStyle] = useState(getStoredNavStyle());
  const [textScale, setTextScale] = useState(getStoredTextScale());
  const [motion, setMotion] = useState(getStoredMotion());

  // system reduced-motion preference (for motion === "system")
  const [systemPrefersReducedMotion, setSystemPrefersReducedMotion] = useState(
    typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e) => setSystemPrefersReducedMotion(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  // system dark preference (for mode === "system")
  const [systemPrefersDark, setSystemPrefersDark] = useState(
    typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
  );

  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => setSystemPrefersDark(e.matches);
    if (mode === "system") {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
  }, [mode]);

  /* ---------- Persist ---------- */
  useEffect(() => {
    try {
      localStorage.setItem("mode", mode);
    } catch {}
  }, [mode]);

  useEffect(() => {
    try {
      localStorage.setItem(BRAND_KEY, JSON.stringify(brand));
    } catch {}
  }, [brand]);

  useEffect(() => {
    try {
      localStorage.setItem("fonts", JSON.stringify(fonts));
    } catch {}
  }, [fonts]);

  useEffect(() => {
    try {
      localStorage.setItem("compact", String(compact));
    } catch {}
  }, [compact]);

  useEffect(() => {
    try {
      localStorage.setItem("navStyle", navStyle);
    } catch {}
  }, [navStyle]);

  useEffect(() => {
    try {
      localStorage.setItem("textScale", String(textScale));
    } catch {}
  }, [textScale]);

  useEffect(() => {
    try {
      localStorage.setItem("motion", motion);
    } catch {}
  }, [motion]);

  /* ---------- Effective reduced motion ---------- */
  const reducedMotion = motion === "system" ? systemPrefersReducedMotion : motion === "reduce";

  useEffect(() => {
    // Las animaciones que no pasan por MUI (la intro con GSAP, CSS propio)
    // leen este atributo en vez de consultar el media query por su cuenta,
    // así la preferencia de la app manda sobre la del sistema.
    document.documentElement.dataset.reducedMotion = reducedMotion ? "true" : "false";
  }, [reducedMotion]);

  /* ---------- Effective dark ---------- */
  const effectiveDark = mode === "system" ? systemPrefersDark : mode === "dark";
  const darkMode = effectiveDark;
  const toggleDarkMode = () => setMode(effectiveDark ? "light" : "dark");

  useEffect(() => {
    // color-scheme para scrollbar nativo, formularios, etc.
    document.documentElement.style.colorScheme = effectiveDark ? "dark" : "light";
    try {
      localStorage.setItem("darkMode", String(effectiveDark));
    } catch {}
  }, [effectiveDark]);

  /* ===================== Theme Factory ===================== */
  const theme = useMemo(() => {
    const paletteMode = effectiveDark ? "dark" : "light";
    const lightBg = { default: "#e6ebf1ff", paper: "#ffffff" };
    const darkBg = { default: "#0b1324", paper: "#121826" };

    const base = createTheme();
    const tertiary = base.palette.augmentColor({ color: { main: "#8b5cf6" } });

    // Tamaños dependientes de compact
    const toolbarMinH = compact ? 44 : 56;
    const btnPad = compact ? "4px 10px" : "6px 14px";
    const btnMinH = compact ? 32 : 36;

    return createTheme({
      // Con "reducir movimiento" las transiciones de MUI pasan a 0 ms en vez de
      // quedar a medias: una duración corta sigue siendo movimiento.
      ...(reducedMotion && {
        transitions: {
          create: () => "none",
          duration: {
            shortest: 0, shorter: 0, short: 0, standard: 0,
            complex: 0, enteringScreen: 0, leavingScreen: 0,
          },
        },
      }),
      palette: {
        mode: paletteMode,
        primary: { main: brand.primary },
        secondary: { main: brand.secondary },
        tertiary,
        background: paletteMode === "light" ? lightBg : darkBg,
      },
      shape: { borderRadius: 14 },
      typography: {
        fontFamily: fonts.body,
        h1: { fontFamily: fonts.heading, fontWeight: 800, letterSpacing: "-.02em" },
        h2: { fontFamily: fonts.heading, fontWeight: 800, letterSpacing: "-.01em" },
        h3: { fontFamily: fonts.heading, fontWeight: 700 },
        h4: { fontFamily: fonts.heading, fontWeight: 700 },
        h5: { fontFamily: fonts.heading, fontWeight: 700 },
        h6: { fontFamily: fonts.heading, fontWeight: 700 },
        button: { textTransform: "none", fontWeight: 600 },
        // tamaños base sutiles; puedes ajustar si quieres más contraste
        fontSize: (compact ? 13.5 : 14.5) * textScale,
        body2: { fontSize: (compact ? 12.5 : 13.5) * textScale },
        caption: { fontSize: (compact ? 11.5 : 12.5) * textScale },
      },
      components: {
        ...(reducedMotion && {
          MuiButtonBase: { defaultProps: { disableRipple: true } },
          MuiSkeleton: { defaultProps: { animation: false } },
        }),
        MuiCssBaseline: {
          styleOverrides: (t) => ({
            body: { backgroundColor: t.palette.background.default },
            code: { fontFamily: fonts.mono },
            pre: { fontFamily: fonts.mono },
            kbd: { fontFamily: fonts.mono },
          }),
        },
        // Drawer — sin override de fondo global; cada drawer maneja su propio bg
        // (el DashboardLayout usa grey[50]/grey[900] según modo)
        MuiDrawer: {
          styleOverrides: {
            paper: { backgroundImage: "none" },
          },
        },
        MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },

        // Navegación / listas — solo density, sin colores que pisen el sidebar
        MuiListItemButton: {
          styleOverrides: {
            root: {
              paddingTop: compact ? 6 : undefined,
              paddingBottom: compact ? 6 : undefined,
            },
          },
        },
        MuiListItemIcon: {
          styleOverrides: {
            root: { "& svg": { fontSize: compact ? 20 : 22 } },
          },
        },

        // AppBar / Toolbar
        MuiToolbar: {
          defaultProps: { variant: compact ? "dense" : undefined },
          styleOverrides: {
            regular: { minHeight: toolbarMinH },
            dense: { minHeight: 44 },
          },
        },

        // Controles comunes
        MuiButton: {
          defaultProps: { size: compact ? "small" : "medium" },
          styleOverrides: { root: { borderRadius: 10, padding: btnPad, minHeight: btnMinH } },
        },
        MuiIconButton: { defaultProps: { size: compact ? "small" : "medium" } },
        MuiTextField: { defaultProps: { size: compact ? "small" : "medium" } },
        MuiFormControl: { defaultProps: { size: compact ? "small" : "medium", margin: compact ? "dense" : "normal" } },
        MuiSelect: { defaultProps: { size: compact ? "small" : "medium" } },
        MuiAutocomplete: { defaultProps: { size: compact ? "small" : "medium" } },
        MuiChip: { defaultProps: { size: compact ? "small" : "medium" } },
        MuiPagination: { defaultProps: { size: compact ? "small" : "medium" } },

        // Tablas
        MuiTable: { defaultProps: { size: compact ? "small" : "medium" } },
        MuiTableCell: {
          styleOverrides: {
            sizeSmall: { paddingTop: 4, paddingBottom: 4 },
          },
        },

        // Tabs (útil para navStyle "top")
        MuiTabs: {
          defaultProps: { variant: "scrollable", scrollButtons: true, allowScrollButtonsMobile: true },
        },
        MuiTab: {
          defaultProps: { iconPosition: "start" },
          styleOverrides: {
            root: { minHeight: compact ? 36 : 44 },
          },
        },
      },
    });
  }, [effectiveDark, brand, fonts, compact, textScale, reducedMotion]);

  /* ---------- Meta theme-color + CSS vars ---------- */
  useEffect(() => {
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "theme-color");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", theme.palette.primary.main);

    const r = document.documentElement.style;
    r.setProperty("--brand-primary", theme.palette.primary.main);
    r.setProperty("--brand-secondary", theme.palette.secondary.main);
    r.setProperty("--bg-default", theme.palette.background.default);
    r.setProperty("--text-primary", theme.palette.text.primary);
  }, [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.background.default,
    theme.palette.text.primary,
  ]);

  /* ===================== Provider ===================== */
  return (
    <ThemeContext.Provider
      value={{
        mode,
        setMode,
        darkMode,
        toggleDarkMode,
        brand,
        setBrand,
        fonts,
        setFonts,
        compact,
        setCompact,
        navStyle,
        setNavStyle,
        textScale,
        setTextScale,
        motion,
        setMotion,
        reducedMotion,
      }}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContextProvider;
